/**
 * **Connected board column settings** — rebuilt from the capture
 * (`discovery/ui-reference/editors/board_relation/dialog-settings.png`, 2026-09-10).
 *
 * Measured contents, in order: the title *Connected board column settings*; the sentence
 * *This column is connected to one board*; one row per board (name over workspace) with a
 * **Disconnect** button on the right; a *Connection settings* heading; **Allow linking to
 * multiple items** with an info icon and a toggle; **Turn on two-way connection** with an info
 * icon and a **Turn on** button; a blue banner reading *Only users with access to all connected
 * boards can see this column's contents. Learn more*; and a footer with *Add a mirror column*
 * on the left and a primary **+ Connect more boards** on the right.
 */
import { useState } from 'react';
import type { ColumnSettingsProps } from '../types';
import type { BoardRelationSettings } from '@elizmonday/shared';

export function BoardRelationSettingsDialog({
  settings,
  onChange,
  onCommit,
  onCancel,
}: ColumnSettingsProps<BoardRelationSettings>) {
  // Round 10i: `column.settings` is `null` on a connect-boards column nobody connected yet.
  const [draft, setDraft] = useState<BoardRelationSettings>(() => ({ ...(settings ?? {}) }));
  const boards = draft.boardIds ?? [];
  const update = (next: BoardRelationSettings): void => {
    setDraft(next);
    onChange(next);
  };
  return (
    <div className="ez-relation-settings" data-testid="board-relation-settings">
      <h2 className="ez-relation-settings__title">Connected board column settings</h2>
      <p className="ez-relation-settings__lede">
        {boards.length === 0
          ? 'This column is not connected to a board yet'
          : boards.length === 1
            ? 'This column is connected to one board'
            : `This column is connected to ${String(boards.length)} boards`}
      </p>
      <ul className="ez-relation-settings__boards">
        {boards.map((boardId) => (
          <li key={String(boardId)} className="ez-relation-settings__board">
            <span className="ez-relation-settings__board-name">{`Board ${String(boardId)}`}</span>
            <button
              type="button"
              className="ez-relation-settings__disconnect"
              data-testid={`relation-disconnect-${String(boardId)}`}
              onClick={() =>
                update({ ...draft, boardIds: boards.filter((entry) => String(entry) !== String(boardId)) })
              }
            >
              Disconnect
            </button>
          </li>
        ))}
      </ul>

      <h3 className="ez-relation-settings__section">Connection settings</h3>
      <label className="ez-relation-settings__row">
        <span>Allow linking to multiple items</span>
        <input
          type="checkbox"
          role="switch"
          data-testid="relation-allow-multiple"
          checked={draft.allowMultipleItems !== false}
          onChange={(event) => update({ ...draft, allowMultipleItems: event.target.checked })}
        />
      </label>
      <div className="ez-relation-settings__row">
        <span>Turn on two-way connection</span>
        <button
          type="button"
          className="ez-relation-settings__turn-on"
          data-testid="relation-two-way"
          aria-pressed={draft.allowCreateReflectionColumn === true}
          onClick={() =>
            update({ ...draft, allowCreateReflectionColumn: draft.allowCreateReflectionColumn !== true })
          }
        >
          {draft.allowCreateReflectionColumn === true ? 'Turn off' : 'Turn on'}
        </button>
      </div>

      <p className="ez-relation-settings__banner">
        Only users with access to all connected boards can see this column&apos;s contents.
      </p>

      <div className="ez-relation-settings__footer">
        <button type="button" className="ez-picker__row" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="ez-formula-builder__primary"
          data-testid="relation-save"
          onClick={() => onCommit(draft)}
        >
          Save
        </button>
      </div>
    </div>
  );
}
